import React, { useCallback, useMemo, useContext } from "react"
import clsx from "clsx"
import WarningIcon from "../../../assets/viewonce.svg"
import { Avatar, IconButton } from "@material-ui/core"
import { Forward } from "lucide-react"
import { Block, ExpandMore } from "@material-ui/icons"
import MarkdownWrapper from "../../MarkdownWrapper"
import Call_log from "./renderers/Call_log"
import ExternalAdReplyPreview from "./renderers/ExternalAdReplyPreview"
import useStyles from "../styles"
import renderContactCard from "./renderers/renderContactCard"
import { checkMessageMedia } from "./utils/checkMessageMedia"
import { mensagemAnuncioCtwaExtrair } from "./utils/mensagemAnuncioCtwaExtrair"
import RestrictedMessage from "./renderers/RestrictedMessage"
import DeletedWithoutPermissionMessage from "./renderers/DeletedWithoutPermissionMessage"
import { MessageAck } from "./utils/MessageAck"
import DailyTimestamp from "./utils/DailyTimestamp"
import MessageDivider from "./utils/MessageDivider"
import QuotedMessage from "./renderers/QuotedMessage"
import EphemeralMessage from "./renderers/EphemeralMessage"
import { canais } from "../../../utils/canais"
import { AuthContext } from "../../../context/Auth/AuthContext"
import resolverImagemPerfil from "../../../helpers/resolverImagemPerfil"
import obterIniciaisDoNome from "../../../helpers/obterIniciaisDoNome"
import obterEstiloAvatarAbreviacao from "../../../helpers/obterEstiloAvatarAbreviacao"

const renderAvatarContato = (cImagemPerfil, cNome, className, theme, lEhInterno = false, cNomeAbreviacao = "") => {
  const { cUrlImagem, cTextoAvatar } = resolverImagemPerfil(cImagemPerfil)
  const cTextoExibir = lEhInterno
    ? cTextoAvatar || cNomeAbreviacao || (cNome ? obterIniciaisDoNome(cNome) : null)
    : null

  if (!cUrlImagem && !cTextoExibir) return null

  const nTamanhoAvatar = 24

  return (
    <Avatar
      src={cUrlImagem || undefined}
      className={className}
      style={!cUrlImagem && cTextoExibir ? obterEstiloAvatarAbreviacao(theme, nTamanhoAvatar) : undefined}
    >
      {!cUrlImagem && cTextoExibir}
    </Avatar>
  )
}

/**
 * @typedef {Object} Contact
 * @property {string} id
 * @property {string} name
 * @property {string} [profilePicUrl]
 */

/**
 * @typedef {Object} QuotedMessage
 * @property {string} id
 * @property {string} body
 * @property {boolean} fromMe
 * @property {Contact} [contact]
 * @property {string} mediaType
 * @property {string} [mediaUrl]
 * @property {string} [dataJson]
 */

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} name
 * @property {string} [nomeAbreviacao]
 * @property {string} [imagemPerfil]
 * @property {string} [profile]
 */

/**
 * @typedef {Object} Ticket
 * @property {number} [canalTipoId]
 * @property {number} [responsavelId]
 * @property {number} [usuarioId]
 * @property {User} [responsavel]
 * @property {User} [usuario]
 */

/**
 * @typedef {Object} Message
 * @property {string} id
 * @property {string} body
 * @property {string} createdAt
 * @property {boolean} fromMe
 * @property {number} ack
 * @property {boolean} [isDeleted]
 * @property {boolean} [isEdited]
 * @property {boolean} [encaminhada]
 * @property {string} [mediaType]
 * @property {string} [mediaUrl]
 * @property {string} [fileName]
 * @property {Contact} [contact]
 * @property {QuotedMessage} [quotedMsg]
 * @property {any} [contactsArrayMessage]
 * @property {number} [usuarioId]
 * @property {Ticket} [ticket]
 * @property {User} [user]
 */

/**
 * @typedef {Object} ContactModalData
 * @property {string} name
 * @property {string} phone
 */

/**
 * @typedef {Object} MessageItemProps
 * @property {Message} message
 * @property {Message} previousMessage
 * @property {boolean} isGroup
 * @property {any} theme
 * @property {(e: MouseEvent, message: Message) => void} handleOpenMessageOptionsMenu
 * @property {Function} setInitialValues
 * @property {Function} setContactModalOpen
 * @property {Function} openViewContactsModal
 * @property {React.RefObject} lastMessageRef
 * @property {(theme: any, contactId?: string) => string} conversaGrupoContatoCorGerar
 * @property {(date: string) => string} dataDescricao
 * @property {(date: string) => Date} parseISO
 * @property {(date: Date, format: string) => string} format
 * @property {(a: Date, b: Date) => boolean} isSameDay
 * @property {string|null} [cTextoErroEntrega]
 */

/**
 * Componente responsável por renderizar uma mensagem individual.
 *
 * @param {MessageItemProps} props
 * @returns {JSX.Element | null}
 */

const MessageItem = ({
  message,
  ticket,
  canalTipoId,
  previousMessage,
  isGroup,
  theme,
  handleOpenMessageOptionsMenu,
  setInitialValues,
  setContactModalOpen,
  openViewContactsModal,
  lastMessageRef,
  conversaGrupoContatoCorGerar,
  dataDescricao,
  parseISO,
  format,
  isSameDay,
  cTextoErroEntrega = null,
}) => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)

  const lEhConversaInterna = canalTipoId === canais.interno
  const nUsuarioLogadoId = user?.id

  // Determina se o usuário logado é participante da conversa interna
  const lUsuarioLogadoEhParticipante =
    lEhConversaInterna && (ticket?.responsavelId === nUsuarioLogadoId || ticket?.usuarioId === nUsuarioLogadoId)

  // Determina se a mensagem deve ir para o lado direito
  const lMensagemDoUsuario = (() => {
    if (lEhConversaInterna) {
      // Se o usuário é participante, verifica se a mensagem é dele
      if (lUsuarioLogadoEhParticipante) {
        return message.usuarioId === nUsuarioLogadoId
      }
      // Se o usuário NÃO é participante (está espiando), mensagens do responsável vão para direita
      return message.usuarioId === ticket?.responsavelId
    }
    // Para outros canais, usa o fromMe padrão
    return message.fromMe
  })()

  // Define se deve exibir como grupo:
  // 1. Grupos verdadeiros (isGroup = true)
  // 2. Conversas internas onde o usuário NÃO é participante (está visualizando como admin/supervisor)
  const lExibirComoGrupo = isGroup || (lEhConversaInterna && !lUsuarioLogadoEhParticipante)

  // Obtém informações do remetente para conversas internas
  const oRemetenteInfo = useMemo(() => {
    if (!lEhConversaInterna) return null

    // Se a mensagem tem user associado, usa seus dados
    if (message.usuario) {
      return {
        id: message.usuario?.id,
        name: message.usuario?.name || message.usuario?.nomeAbreviacao || "Usuário",
        profilePicUrl: message.usuario?.imagemPerfil || null,
        nomeAbreviacao: message.usuario?.nomeAbreviacao || "",
      }
    }

    // Fallback: tenta buscar do ticket
    const nRemetenteId = message.usuarioId

    if (nRemetenteId === ticket?.responsavelId && ticket?.responsavel) {
      return {
        id: ticket.responsavel.id,
        name: ticket.responsavel.name || ticket.responsavel.nomeAbreviacao || "Responsável",
        profilePicUrl: ticket.responsavel.imagemPerfil || null,
        nomeAbreviacao: ticket.responsavel.nomeAbreviacao || "",
      }
    }

    if (nRemetenteId === ticket?.usuarioId && ticket?.usuario) {
      return {
        id: ticket.usuario.id,
        name: ticket.usuario.name || ticket.usuario.nomeAbreviacao || "Usuário",
        profilePicUrl: ticket.usuario.imagemPerfil || null,
        nomeAbreviacao: ticket.usuario.nomeAbreviacao || "",
      }
    }

    return {
      id: nRemetenteId,
      name: "Usuário",
      profilePicUrl: null,
      nomeAbreviacao: "",
    }
  }, [lEhConversaInterna, message, ticket])

  // Memoização da cor do contato/usuário
  const contactColor = useMemo(() => {
    if (lEhConversaInterna && lExibirComoGrupo) {
      // Para conversas internas exibidas como grupo, gera cor baseada no ID do remetente
      return conversaGrupoContatoCorGerar(theme, oRemetenteInfo?.id?.toString())
    }
    // Para grupos normais, usa o ID do contato
    return conversaGrupoContatoCorGerar(theme, message.contact?.id)
  }, [lEhConversaInterna, lExibirComoGrupo, oRemetenteInfo, theme, message.contact?.id, conversaGrupoContatoCorGerar])

  // Memoização de flags booleanas
  const messageFlags = useMemo(
    () => ({
      isViewOnce: message.body === "ERR_VIEW_ONCE_MESSAGE",
      isRestricted: message.body === "_INFO_MESSAGE_RESTRICTED_INFO_",
      isEphemeral: message.body === "_INFO_PROTOCOL_EPHEMERAL_SETTING_INFO_",
      isSticker: message.body === "_INFO_STICKER_INFO_",
      isDeletedWithoutPermission: message.body === "_INFO_MESSAGE_DELETED_INFO_",
      isForwarded: message.encaminhada,
      isDeleted: message.isDeleted,
      isMedia:
        (message.mediaUrl && message.mediaType !== "call_log") ||
        message.mediaType === "locationMessage" ||
        message.mediaType === "liveLocationMessage",
    }),
    [message],
  )

  const oAnuncioCtwa = useMemo(() => mensagemAnuncioCtwaExtrair(message.dataJson), [message.dataJson])

  // Usa lMensagemDoUsuario ao invés de message.fromMe para definir a classe
  const messageClass = lMensagemDoUsuario ? classes.messageRight : classes.messageLeft

  const lMensagemApagadaTemPreview = useMemo(() => {
    if (!messageFlags.isDeleted) return false

    const cBody = typeof message.body === "string" ? message.body : ""
    const { mediaType, mediaUrl, dataJson } = message

    if (mediaUrl && mediaType !== "call_log") return true

    if (["locationMessage", "liveLocationMessage"].includes(mediaType)) {
      return cBody.includes("|") || Boolean(dataJson) || cBody.startsWith("data:image/")
    }

    if (["contactMessage", "contactsArrayMessage"].includes(mediaType)) {
      return cBody.includes("BEGIN:VCARD") || Boolean(dataJson)
    }

    return false
  }, [message, messageFlags.isDeleted])

  const lEhMensagemApagadaComMidia =
    messageFlags.isMedia ||
    ["contactMessage", "contactsArrayMessage", "locationMessage", "liveLocationMessage"].includes(message.mediaType)

  const lMidiaApagadaSomenteTarja = messageFlags.isDeleted && !lMensagemApagadaTemPreview && lEhMensagemApagadaComMidia

  const lMidiaApagadaComPreview = messageFlags.isDeleted && lMensagemApagadaTemPreview
  const lExibirErroEntrega = Boolean(cTextoErroEntrega) && !lEhConversaInterna && !messageFlags.isDeleted
  const oAvisoErroEntrega = lExibirErroEntrega ? (
    <span className={classes.erroEntregaAviso}>{cTextoErroEntrega}</span>
  ) : null
  const oMessageAck = (
    <MessageAck
      ack={message.ack}
      isDeleted={messageFlags.isDeleted}
      fromMe={lEhConversaInterna ? lMensagemDoUsuario && lUsuarioLogadoEhParticipante : lMensagemDoUsuario}
      theme={theme}
      classes={classes}
    />
  )

  // Validação do body
  const shouldHideBody = useMemo(() => {
    if (message.body.startsWith("_INFO_") && message.body.endsWith("_INFO_")) return true
    if (message.body === message?.mediaUrl?.split("/")?.pop()) return true
    if (
      message.mediaType === "audio" &&
      /^audio-record-site-\d+\.mp3$/i.test(
        String(message.body || "")
          .replace(/^\*[^*\n]+:\*\s*/u, "")
          .trim(),
      )
    )
      return true
    if (message.body === "_INFO_STICKER_INFO_" && message.mediaUrl) return true
    if (
      ["locationMessage", "liveLocationMessage", "contactMessage", "contactsArrayMessage"].includes(message.mediaType)
    )
      return true
    return false
  }, [message])

  // Callback para menu de opções
  const handleContextMenu = useCallback(
    (e) => {
      if (!messageFlags.isDeleted) {
        handleOpenMessageOptionsMenu(e, message)
      }
    },
    [messageFlags.isDeleted, handleOpenMessageOptionsMenu, message],
  )

  const handleMenuClick = useCallback(
    (e) => {
      handleOpenMessageOptionsMenu(e, message)
    },
    [handleOpenMessageOptionsMenu, message],
  )

  // Early returns para casos especiais
  if (!message || !message.createdAt) return null

  // call_log (novo) ou legado: registro de chamada salvo sem mediaType
  const lCallLog =
    message.mediaType === "call_log" ||
    (typeof message.body === "string" && /^Chamada de voz\/vídeo (atendida|rejeitada|perdida)/i.test(message.body))

  if (lCallLog) {
    return (
      <Call_log
        key={message.id}
        message={message}
        renderDailyTimestamps={() => (
          <DailyTimestamp
            message={message}
            previousMessage={previousMessage}
            isSameDay={isSameDay}
            parseISO={parseISO}
            dataDescricao={dataDescricao}
            classes={classes}
          />
        )}
        renderMessageDivider={() => (
          <MessageDivider
            message={message}
            previousMessage={previousMessage}
            classes={classes}
            lEhConversaInterna={lEhConversaInterna}
            lUsuarioLogadoEhParticipante={lUsuarioLogadoEhParticipante}
          />
        )}
        classes={classes}
        isGroup={lExibirComoGrupo}
        fromMe={lMensagemDoUsuario}
        theme={theme}
      />
    )
  }

  if (messageFlags.isEphemeral) {
    return (
      <React.Fragment key={message.id}>
        <DailyTimestamp
          message={message}
          previousMessage={previousMessage}
          isSameDay={isSameDay}
          parseISO={parseISO}
          dataDescricao={dataDescricao}
          classes={classes}
        />
        <MessageDivider
          message={message}
          previousMessage={previousMessage}
          classes={classes}
          lEhConversaInterna={lEhConversaInterna}
          lUsuarioLogadoEhParticipante={lUsuarioLogadoEhParticipante}
        />
        <div id={message.id}>
          <EphemeralMessage message={message} classes={classes} />
        </div>
      </React.Fragment>
    )
  }

  if (messageFlags.isRestricted) {
    return (
      <RestrictedMessage
        classes={classes}
        message={message}
        previousMessage={previousMessage}
        messageClass={messageClass}
        isGroup={lExibirComoGrupo}
        isSameDay={isSameDay}
        dataDescricao={dataDescricao}
        lEhConversaInterna={lEhConversaInterna}
        lUsuarioLogadoEhParticipante={lUsuarioLogadoEhParticipante}
      />
    )
  }

  if (messageFlags.isDeletedWithoutPermission) {
    return (
      <DeletedWithoutPermissionMessage
        classes={classes}
        message={message}
        previousMessage={previousMessage}
        messageClass={messageClass}
        isGroup={lExibirComoGrupo}
        isSameDay={isSameDay}
        dataDescricao={dataDescricao}
        parseISO={parseISO}
        format={format}
        lEhConversaInterna={lEhConversaInterna}
        lUsuarioLogadoEhParticipante={lUsuarioLogadoEhParticipante}
        theme={theme}
        lMensagemDoUsuario={lMensagemDoUsuario}
      />
    )
  }

  // Renderização da mensagem comum
  return (
    <React.Fragment key={message.id}>
      <DailyTimestamp
        message={message}
        previousMessage={previousMessage}
        isSameDay={isSameDay}
        parseISO={parseISO}
        dataDescricao={dataDescricao}
        classes={classes}
      />
      <MessageDivider
        message={message}
        previousMessage={previousMessage}
        classes={classes}
        lEhConversaInterna={lEhConversaInterna}
        lUsuarioLogadoEhParticipante={lUsuarioLogadoEhParticipante}
      />

      <div
        id={message.id}
        data-testid="message-bubble"
        className={clsx(messageClass, {
          [classes.viewOnceMessage]: !lMensagemDoUsuario && messageFlags.isViewOnce,
          [classes.viewOnceMessageRight]: lMensagemDoUsuario && messageFlags.isViewOnce,
          [classes.deletedMessage]: messageFlags.isDeleted,
        })}
        onContextMenu={handleContextMenu}
        ref={lastMessageRef}
      >
        <IconButton
          variant="contained"
          size="small"
          id="messageActionsButton"
          disabled={message.isDeleted}
          className={classes.messageActionsButton}
          onClick={handleMenuClick}
        >
          <ExpandMore />
        </IconButton>

        <div className={classes.messageContent}>
          {/* Exibe informações do remetente se for grupo OU conversa interna onde usuário não participa */}
          {lExibirComoGrupo && (
            <div className={classes.groupContactInfo}>
              {lEhConversaInterna ? (
                <>
                  {renderAvatarContato(
                    oRemetenteInfo?.profilePicUrl,
                    oRemetenteInfo?.name,
                    classes.contactAvatar,
                    theme,
                    true,
                    oRemetenteInfo?.nomeAbreviacao,
                  )}
                  <span className={classes.groupMessageContactName} style={{ color: contactColor }}>
                    {oRemetenteInfo?.name || "Usuário"}
                  </span>
                </>
              ) : (
                <>
                  {!lMensagemDoUsuario && message.contact?.profilePicUrl && (
                    <Avatar src={message.contact.profilePicUrl} className={classes.contactAvatar} />
                  )}
                  {!lMensagemDoUsuario && (
                    <span className={classes.groupMessageContactName} style={{ color: contactColor }}>
                      {message.contact?.name}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {messageFlags.isForwarded && (
            <div className={classes.forwardedMessage}>
              <Forward style={{ width: 16, height: 16, marginRight: 4 }} />
              <span>Encaminhada</span>
            </div>
          )}

          {message.quotedMsg && (
            <div className={classes.textContentItem} style={{ maxWidth: "350px" }}>
              <QuotedMessage message={message} lEhConversaInterna={lEhConversaInterna} classes={classes} />
            </div>
          )}

          {oAnuncioCtwa?.modo === "card" && (
            <ExternalAdReplyPreview
              cTitulo={oAnuncioCtwa.cTitulo}
              cImagemUrl={oAnuncioCtwa.cImagemUrl}
              cSourceUrl={oAnuncioCtwa.cSourceUrl}
              cBody={oAnuncioCtwa.cBody}
              className={classes.anuncioCtwaCard}
            />
          )}
          {oAnuncioCtwa?.modo === "fallback" && (
            <div className={classes.anuncioCtwaFallback}>{oAnuncioCtwa.cTexto}</div>
          )}

          {!lMidiaApagadaSomenteTarja &&
            checkMessageMedia(
              message,
              classes,
              messageFlags.isDeleted,
              messageFlags.isMedia,
              messageFlags.isViewOnce,
              messageFlags.isSticker,
              window.innerWidth < 600,
            )}

          {!lMidiaApagadaSomenteTarja &&
            (message.mediaType === "contactMessage" || message.mediaType === "contactsArrayMessage") &&
            renderContactCard(message, classes, setInitialValues, setContactModalOpen, openViewContactsModal)}

          {lMidiaApagadaSomenteTarja ? (
            <div>
              <span className={classes.textContentItemDeleted} style={{ display: "flex", alignItems: "center" }}>
                <Block color="disabled" fontSize="small" className={classes.deletedIcon} />
                Essa mensagem foi apagada &nbsp;
              </span>
            </div>
          ) : lMidiaApagadaComPreview ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                maxWidth: 350,
                width: "100%",
                padding: "3px 6px 6px",
              }}
            >
              <span
                className={classes.textContentItemDeleted}
                style={{ display: "flex", alignItems: "center", flex: 1, padding: "3px 0 6px 6px" }}
              >
                <Block color="disabled" fontSize="small" className={classes.deletedIcon} />
                Essa mensagem foi apagada &nbsp;
              </span>
              <span className={classes.timestamp} style={{ marginTop: 0, paddingBottom: 0, flexShrink: 0 }}>
                {format(parseISO(message.createdAt), "HH:mm")}
                {oAvisoErroEntrega}
                {oMessageAck}
              </span>
            </div>
          ) : messageFlags.isViewOnce ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <img src={WarningIcon} alt="Warning" style={{ marginRight: 8, height: 32, width: 32 }} />
              <div className={classes.textContentItem}>
                <MarkdownWrapper>
                  Você {lMensagemDoUsuario ? "enviou" : "recebeu"} uma mensagem de visualização única. Por motivos de
                  privacidade, você só pode abrir essa mensagem no seu celular.
                </MarkdownWrapper>
              </div>
            </div>
          ) : (
            <div
              className={clsx(classes.textContentItem, {
                [classes.textContentItemDeleted]: message.isDeleted,
                [classes.textContentItemEdited]: message.isEdited,
              })}
              style={{ maxWidth: "350px" }}
            >
              <MarkdownWrapper>{shouldHideBody ? null : message.body}</MarkdownWrapper>
            </div>
          )}

          {!lMidiaApagadaComPreview && (
            <span className={classes.timestamp}>
              {message.isEdited && !message.isDeleted && <span>Editada </span>}
              {format(parseISO(message.createdAt), "HH:mm")}
              {oAvisoErroEntrega}
              {oMessageAck}
            </span>
          )}
        </div>
      </div>
    </React.Fragment>
  )
}

export default React.memo(MessageItem, (prevProps, nextProps) => {
  // Comparação customizada para evitar re-renders desnecessários
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.ack === nextProps.message.ack &&
    prevProps.message.isDeleted === nextProps.message.isDeleted &&
    prevProps.message.isEdited === nextProps.message.isEdited &&
    prevProps.message.body === nextProps.message.body &&
    prevProps.message.dataJson === nextProps.message.dataJson &&
    prevProps.message.usuarioId === nextProps.message.usuarioId &&
    prevProps.canalTipoId === nextProps.canalTipoId &&
    prevProps.cTextoErroEntrega === nextProps.cTextoErroEntrega &&
    prevProps.previousMessage?.id === nextProps.previousMessage?.id &&
    prevProps.previousMessage?.usuarioId === nextProps.previousMessage?.usuarioId
  )
})
